import React, { ChangeEvent } from 'react';
import { ButtonBase, InputBase, Popper } from '@material-ui/core';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import { FaCheck, FaWindowClose, FaWrench } from 'react-icons/fa';
import { ShopArray } from '../../fetch/fetchInbound';
import { ShowShops } from './ShowShops';
import { fade, makeStyles } from '@material-ui/core/styles';
export const useShopArrayStyles = makeStyles(theme => ({
  root: {
    width: 221,
    fontSize: 13,
  },
  button: {
    fontSize: 13,
    width: '100%',
    textAlign: 'left',
    paddingBottom: 8,
    color: '#586069',
    fontWeight: 600,
    '&:hover,&:focus': {
      color: '#0f6474',
    },
    '& span': {
      width: '100%',
    },
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  tag: {
    marginTop: 3,
    height: 20,
    padding: '.15em 4px',
    fontWeight: 600,
    lineHeight: '15px',
    borderRadius: 2,
    backgroundColor: '#4d4d4d', //theme.palette.primary.dark,
    color: '#ffffff', // theme.palette.primary.contrastText,
  },
  popper: {
    border: '1px solid rgba(27,31,35,.15)',
    boxShadow: '0 3px 12px rgba(27,31,35,.15)',
    borderRadius: 3,
    width: 300,
    zIndex: 1,
    fontSize: 13,
    color: '#586069',
    backgroundColor: '#f6f8fa',
  },
  header: {
    borderBottom: '1px solid #e1e4e8',
    padding: '8px 10px',
    fontWeight: 600,
  },
  inputBase: {
    padding: 10,
    width: '100%',
    borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      border: '1px solid #ced4da',
      fontSize: 14,
      '&:focus': {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  paper: {
    boxShadow: 'none',
    margin: 0,
    color: '#586069',
    fontSize: 13,
  },
  option: {
    minHeight: 'auto',
    alignItems: 'flex-start',
    padding: 8,
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  popperDisablePortal: {
    position: 'relative',
  },
  iconSelected: {
    width: 17,
    height: 17,
    marginRight: 5,
    marginLeft: -2,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  text: {
    flexGrow: 1,
  },
  close: {
    opacity: 0.6,
    width: 18,
    height: 18,
  },
}));

export default function ShopArrayComboBox({
  placeHolderText,
  shopList = [],
  shopArrayList,
  multiple = false,
  addShopMethod,
}: {
  placeHolderText?: string;
  shopList?: ShopArray[];
  shopArrayList: ShopArray[];
  multiple?: boolean;
  addShopMethod: (shop: ShopArray[]) => void;
}): JSX.Element {
  const classes = useShopArrayStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingValue, setPendingValue] = React.useState<ShopArray[]>(shopList);

  const handleClose = (event, reason) => {
    if (reason === 'toggleInput') {
      return;
    }
    addShopMethod(pendingValue);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'shop-label' : undefined;

  return (
    <React.Fragment>
      <div className={classes.root}>
        <ButtonBase
          disableRipple
          className={classes.button}
          aria-describedby={id}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <span>Shops</span>
          <FaWrench />
        </ButtonBase>
        <ShowShops shopList={shopList} />
      </div>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        <div className={classes.header}>Edit shop list</div>
        <Autocomplete
          open
          onClose={handleClose}
          multiple
          classes={{
            paper: classes.paper,
            option: classes.option,
            popperDisablePortal: classes.popperDisablePortal,
          }}
          value={pendingValue}
          getOptionSelected={(option: ShopArray) => {
            if (pendingValue.map(obj => obj.id).indexOf(option.id) !== -1) {
              return true;
            } else {
              return false;
            }
          }}
          onChange={(
            // eslint-disable-next-line @typescript-eslint/ban-types
            event: ChangeEvent<{}>,
            newValue: Array<string | ShopArray>,
            reason: AutocompleteChangeReason,
            details: AutocompleteChangeDetails<ShopArray>
          ) => {
            if (reason === 'select-option') {
              const newValueNonNull = (newValue as ShopArray[]).filter(
                (obj: ShopArray) => obj !== null
              );
              setPendingValue(newValueNonNull);
            } else if (reason === 'remove-option') {
              const newArray: ShopArray[] = pendingValue
                .filter(obj => obj.id !== details.option.id)
                .filter(obj => obj !== null);
              setPendingValue(newArray);
            }
          }}
          disableCloseOnSelect
          disablePortal
          disableClearable
          renderTags={() => null}
          noOptionsText="No shops fetched"
          renderOption={(option: ShopArray, { selected }) => {
            return (
              <React.Fragment>
                <FaCheck
                  className={classes.iconSelected}
                  style={{
                    visibility: selected ? 'visible' : 'hidden',
                  }}
                />
                <span
                  className={classes.color}
                  style={{ backgroundColor: '#00b6bc' }} //option.color }}
                />
                <div className={classes.text}>{option.shopName}</div>
                <FaWindowClose
                  className={classes.close}
                  style={{
                    visibility: selected ? 'visible' : 'hidden',
                  }}
                />
              </React.Fragment>
            );
          }}
          options={shopArrayList.sort((a: ShopArray, b: ShopArray) => {
            // Display the selected labels first.
            let ai = pendingValue.indexOf(a);
            ai =
              ai === -1 ? pendingValue.length + shopArrayList.indexOf(a) : ai;
            let bi = pendingValue.indexOf(b);
            bi =
              bi === -1 ? pendingValue.length + shopArrayList.indexOf(b) : bi;
            return ai - bi;
          })}
          getOptionLabel={(option: ShopArray) => option.shopName}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <InputBase
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              autoFocus
              className={classes.inputBase}
            />
          )}
        />
      </Popper>
    </React.Fragment>
  );
}
